// frontend/src/lib/rbac-map.ts — DATOS PUROS (sin React): ruta → área, página → pestaña → capacidad.
// Lo consume lib/rbac.ts (UI) y scripts/frontend-rbac-coverage.test.ts (prueba). Las keys son las del catálogo.

/** Ruta de App.tsx → capacidad que habilita la pantalla. */
export const AREA_OF_ROUTE: Record<string, string> = {
  '/': 'dashboard.view',
  '/orders': 'orders.view',
  '/questions': 'questions.view',
  '/messages': 'messages.view',
  '/assistant': 'assistant.view',
  '/catalog': 'catalog.view',
  '/catalog/:itemId': 'catalog.view',
  '/suppliers': 'purchases.view',
  '/purchases': 'purchases.view',
  '/sales': 'sales.view',
  '/finance': 'finance.view',
  '/reports': 'reports.view',
  '/mercadolibre': 'ml.view',
  '/amazon': 'amazon.view',
  '/warehouse': 'warehouse.view',
  '/ai': 'ai.view',
  '/contacts': 'contacts.view_suppliers', // ver §Task 5: la pestaña Compradores exige contacts.view_buyers
  '/logistics': 'logistics.view',
  '/publisher': 'publisher.view',
  '/settings': 'settings.general', // cualquier pestaña de settings otorgada da entrada; ver TAB_CAP.settings
  '/admin/health': 'health.view',
  '/admin/reviews': 'reviews.view',
  '/admin/pricing-losses': 'pricing_losses.view',
  '/admin/price-monitor': 'price_monitor_runs.view',
};

/** Página → key de pestaña (tal cual en el array `tabs` del archivo) → capacidad. */
export const TAB_CAP: Record<string, Record<string, string>> = {
  purchases: {
    amazon: 'purchases.tab.amazon',
    pos: 'purchases.tab.pos',
    'internal-pos': 'purchases.tab.internal_pos',
    'internal-stock': 'purchases.tab.internal_stock',
    'internal-movements': 'purchases.tab.movements',
    orphans: 'purchases.tab.orphans',
    suppliers: 'purchases.tab.suppliers',
  },
  warehouse: {
    batches: 'warehouse.tab.batches',
    bags: 'warehouse.tab.bags',
    ready: 'warehouse.tab.ready',
    alerts: 'warehouse.tab.alerts',
    metrics: 'warehouse.tab.metrics',
    history: 'warehouse.tab.history',
    all: 'warehouse.tab.all_sellers',
  },
  // La key real de la pestaña de productos es 'productos' (pages/catalog/index.tsx).
  catalog: { productos: 'catalog.tab.products', clips: 'catalog.tab.clips' },
  catalogDetail: {
    general: 'catalog.tab.detail_general',
    // La key real es 'edit' (TABS en pages/catalog/CatalogDetailPage.tsx); la capacidad se llama detail_edit_ml.
    edit: 'catalog.tab.detail_edit_ml',
    pricing: 'catalog.tab.detail_pricing',
    stock: 'catalog.tab.detail_stock',
    purchases: 'catalog.tab.detail_purchases',
    sites: 'catalog.tab.detail_sites',
    performance: 'catalog.tab.detail_performance',
    metadata: 'catalog.tab.detail_metadata',
  },
  publisher: {
    queue: 'publisher.tab.queue',
    drafts: 'publisher.tab.drafts',
    published: 'publisher.tab.published',
    feeder: 'publisher.tab.feeder',
    topsellers: 'publisher.tab.topsellers',
  },
  assistant: {
    chat: 'assistant.tab.chat',
    emails: 'assistant.tab.emails',
    mailboxes: 'assistant.tab.mailboxes',
    directives: 'assistant.tab.directives',
    inbox: 'assistant.tab.inbox',
    cases: 'assistant.tab.cases',
    actions: 'assistant.tab.actions',
    capabilities: 'assistant.tab.capabilities',
    decisions: 'assistant.tab.decisions',
    brain: 'assistant.tab.brain',
  },
  finance: {
    summary: 'finance.tab.summary',
    monthly: 'finance.tab.monthly',
    daily: 'finance.tab.daily',
    top: 'finance.tab.top',
    sites: 'finance.tab.sites',
    amazonSpend: 'finance.tab.amazon_spend',
    pnl: 'finance.tab.pnl',
    ledger: 'finance.tab.ledger',
    mpRecon: 'finance.tab.mp_recon',
    cancellations: 'finance.tab.cancellations',
    pendingClaims: 'finance.tab.pending_claims',
  },
  reports: {
    sales: 'reports.tab.sales',
    margins: 'reports.tab.margins',
    inventory: 'reports.tab.inventory',
    purchases: 'reports.tab.purchases',
  },
  ml: {
    listings: 'ml.tab.listings',
    orders: 'ml.tab.orders',
    questions: 'ml.tab.questions',
    claims: 'ml.tab.claims',
    promotions: 'ml.tab.promotions',
    ads: 'ml.tab.ads',
    metrics: 'ml.tab.metrics',
  },
  amazon: {
    purchases: 'amazon.tab.purchases',
    orders: 'amazon.tab.orders',
    shipments: 'amazon.tab.shipments',
    'price-monitor': 'amazon.tab.price_monitor',
    catalog: 'amazon.tab.catalog',
  },
  ai: {
    enricher: 'ai.tab.enricher',
    automation: 'ai.tab.automation',
    processes: 'ai.tab.processes',
    markets: 'ai.tab.markets',
    directives: 'ai.tab.directives',
  },
  settings: {
    general: 'settings.general',
    appearance: 'settings.appearance',
    connections: 'settings.connections',
    mlControl: 'settings.ml_control',
    apiCredentials: 'settings.credentials',
    automations: 'ai.view',
    notifications: 'settings.notifications',
    users: 'settings.users_roles',
    businessRules: 'settings.business_rules',
    dataSources: 'settings.data_sources',
    telegram: 'settings.telegram',
    markupRules: 'settings.markup_rules',
    relist: 'settings.relist',
    trendDiscovery: 'settings.trend_discovery',
    labels: 'settings.printing',
    warehouse: 'settings.warehouse',
    audit: 'settings.audit',
  },
  // Sub-pantalla de Ajustes: sus tres pestañas se abren con la MISMA llave que da entrada a la pantalla.
  usersRoles: { users: 'settings.users_roles', roles: 'settings.users_roles', pending: 'settings.users_roles' },
  sales: { orders: 'sales.view', claims: 'sales.view' },
  contacts: { buyers: 'contacts.view_buyers', suppliers: 'contacts.view_suppliers' },
  logistics: { shipments: 'logistics.view', labels: 'logistics.view', tracking: 'logistics.view' },
};

/** Orden en que se busca la primera pantalla disponible para un usuario (mismo orden del menú lateral,
 *  y después el resto de las pantallas que no están en el menú). Fuente ÚNICA de ese orden. */
export const NAV_ORDER: string[] = [
  '/', '/orders', '/questions', '/messages', '/assistant', '/purchases', '/warehouse', '/catalog', '/publisher',
  '/finance', '/admin/reviews', '/admin/pricing-losses', '/admin/health', '/admin/price-monitor',
  '/settings', '/sales', '/mercadolibre', '/amazon', '/ai', '/contacts', '/logistics', '/reports',
];

/** Rutas que se abren con CUALQUIERA de varias capacidades (la pantalla tiene pestañas independientes
 *  y basta con tener una concedida). El resto de las rutas usa AREA_OF_ROUTE. */
export const ROUTE_ANY_CAPS: Record<string, string[]> = {
  '/settings': Object.values(TAB_CAP.settings),
  '/contacts': Object.values(TAB_CAP.contacts),
};

/** Dónde vive la declaración de pestañas de cada página y cómo extraer sus keys (para la prueba de cobertura).
 *  `block` recorta (grupo 1) el trozo de archivo donde están declaradas; `keyRegex` saca cada key de ese trozo.
 *  Se ancla siempre en la declaración real (unión de tipo, VALID_TABS o el array `tabs`), nunca en la FORMA
 *  de los objetos: así, si mañana una pestaña agrega o saca un campo, la prueba la sigue viendo. */
export interface TabSource {
  file: string;
  block: RegExp;
  keyRegex: RegExp;
}

/** Unión de tipo: `type X = 'a' | 'b';` */
const union = (typeName: string) => new RegExp(`type ${typeName}\\s*=\\s*([^;]+);`);
/** Array declarado: `const tabs … = [ … ];` (también `const TABS`). */
const arrayDecl = (name: string) => new RegExp(`const ${name}[^=]*=\\s*\\[([\\s\\S]*?)\\n\\s*\\];`);
/** Keys sueltas entre comillas (uniones, VALID_TABS). Incluye dígitos y guion bajo: si mañana
 *  una pestaña se llama `mp_recon` o `top500`, la prueba la sigue viendo en vez de ignorarla. */
const QUOTED = /'([\w-]+)'/g;
/** Keys dentro de un array de objetos de pestaña. */
const KEY_FIELD = /key:\s*'([a-zA-Z-]+)'/g;

export const PAGE_TAB_FILES: Record<string, TabSource> = {
  purchases: { file: 'pages/purchases/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  warehouse: { file: 'pages/warehouse/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  finance: { file: 'pages/finance/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  amazon: { file: 'pages/amazon/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  ai: { file: 'pages/ai/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  logistics: { file: 'pages/logistics/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  reports: { file: 'pages/reports/index.tsx', block: union('ReportType'), keyRegex: QUOTED },
  // La lista blanca de pestañas es la fuente de verdad de la página.
  ml: { file: 'pages/mercadolibre/index.tsx', block: /const VALID_TABS\s*=\s*\[([^\]]+)\]/, keyRegex: QUOTED },
  publisher: { file: 'pages/publisher/index.tsx', block: arrayDecl('tabs'), keyRegex: KEY_FIELD },
  assistant: { file: 'pages/assistant/index.tsx', block: arrayDecl('tabs'), keyRegex: KEY_FIELD },
  settings: { file: 'pages/settings/index.tsx', block: arrayDecl('tabs'), keyRegex: KEY_FIELD },
  catalogDetail: { file: 'pages/catalog/CatalogDetailPage.tsx', block: arrayDecl('TABS'), keyRegex: KEY_FIELD },
  // El array de pestañas está escrito dentro del JSX: `{[ … ].map(tab => …)}`.
  catalog: { file: 'pages/catalog/index.tsx', block: /\{\[\s*([\s\S]*?)\]\.map\(tab\b/, keyRegex: KEY_FIELD },
  sales: { file: 'pages/sales/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  usersRoles: { file: 'pages/settings/users-roles/index.tsx', block: union('Tab'), keyRegex: QUOTED },
  // Contactos declara sus pestañas en el JSX: `(['buyers', 'suppliers'] as const).map(tab => …)`.
  contacts: { file: 'pages/contacts/index.tsx', block: /\(\[([^\]]+)\]\s*as const\)\.map\(tab\b/, keyRegex: QUOTED },
};
